import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { geoCentroid } from "d3-geo";
import type { GlobeMethods } from "react-globe.gl";
import { feature } from "topojson-client";
import { MeshBasicMaterial } from "three";
import worldAtlas from "world-atlas/countries-110m.json";
import type { Feature, FeatureCollection, Geometry } from "geojson";
import type { Slot } from "../types";

type CountryFeature = Feature<Geometry, { name?: string }> & {
  id?: string | number;
  slot?: Slot;
  center: [number, number];
};

type ArcDatum = {
  startLat: number;
  startLng: number;
  endLat: number;
  endLng: number;
  color: [string, string];
};

const Globe = lazy(() => import("react-globe.gl"));

interface EarthGlobeProps {
  slots: Slot[];
  selectedSlot?: Slot;
  onSelect: (slot: Slot) => void;
  onHover: (country: string) => void;
}

export default function EarthGlobe({ slots, selectedSlot, onSelect, onHover }: EarthGlobeProps) {
  const globeRef = useRef<GlobeMethods>();
  const stageRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 720, height: 500 });
  const reducedMotion = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const capMaterials = useMemo(() => ({
    selected: new MeshBasicMaterial({ color: "#ffb35c" }),
    ranked: new MeshBasicMaterial({ color: "#6fd4aa" }),
    open: new MeshBasicMaterial({ color: "#203e45" }),
    quiet: new MeshBasicMaterial({ color: "#132a32" }),
  }), []);

  const countries = useMemo(() => {
    const slotById = new Map(slots.map((slot) => {
      const rawId = slot.id.replace("country-", "");
      return [String(Number(rawId) || rawId), slot];
    }));
    const collection = feature(
      worldAtlas as never,
      worldAtlas.objects.countries as never,
    ) as unknown as FeatureCollection<Geometry, { name?: string }>;

    return collection.features.map((country) => {
      const center = geoCentroid(country) as [number, number];
      return {
        ...country,
        slot: slotById.get(String(Number(country.id) || country.id)),
        center,
      } as CountryFeature;
    });
  }, [slots]);

  const selectedCountry = useMemo(
    () => countries.find((country) => country.slot?.id === selectedSlot?.id),
    [countries, selectedSlot?.id],
  );

  const arcs = useMemo<ArcDatum[]>(() => {
    if (!selectedCountry) return [];
    const [endLng, endLat] = selectedCountry.center;
    const active = countries.filter((country) => country.slot?.entries.length && country !== selectedCountry);
    const origins = active.length
      ? active.slice(0, 14)
      : countries.filter((country) => ["840", "356", "076", "826", "392"].includes(String(country.id)));
    return origins.map((country) => ({
      startLat: country.center[1],
      startLng: country.center[0],
      endLat,
      endLng,
      color: ["rgba(255,255,255,0)", "#ffb35c"],
    }));
  }, [countries, selectedCountry]);

  const points = useMemo(
    () => countries
      .filter((country) => country.slot?.entries.length || country === selectedCountry)
      .map((country) => ({
        country,
        lat: country.center[1],
        lng: country.center[0],
        selected: country === selectedCountry,
      })),
    [countries, selectedCountry],
  );

  useEffect(() => {
    if (!stageRef.current) return;
    const observer = new ResizeObserver(([entry]) => {
      setSize({
        width: Math.max(300, Math.floor(entry.contentRect.width)),
        height: Math.max(360, Math.floor(entry.contentRect.height)),
      });
    });
    observer.observe(stageRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!selectedCountry || !globeRef.current) return;
    globeRef.current.pointOfView(
      { lat: selectedCountry.center[1] - 8, lng: selectedCountry.center[0], altitude: 1.75 },
      reducedMotion ? 0 : 1100,
    );
  }, [reducedMotion, selectedCountry]);

  function configureGlobe() {
    if (!globeRef.current) return;
    globeRef.current.renderer().setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    const controls = globeRef.current.controls();
    controls.autoRotate = !reducedMotion;
    controls.autoRotateSpeed = 0.28;
    controls.enableDamping = true;
    controls.dampingFactor = 0.06;
    controls.minDistance = 160;
    controls.maxDistance = 390;
  }

  return (
    <div className="earth-globe-stage" ref={stageRef}>
      <Suspense fallback={<div className="earth-globe-loading">BUILDING THE WORLD…</div>}>
        <Globe
        ref={globeRef}
        width={size.width}
        height={size.height}
        backgroundColor="rgba(0,0,0,0)"
        showAtmosphere
        atmosphereColor="#74d5ff"
        atmosphereAltitude={0.18}
        showGraticules
        polygonsData={countries}
        polygonCapMaterial={(datum) => {
          const country = datum as CountryFeature;
          if (country === selectedCountry) return capMaterials.selected;
          if (country.slot?.entries.length) return capMaterials.ranked;
          if (country.slot) return capMaterials.open;
          return capMaterials.quiet;
        }}
        polygonCapColor={(datum) => {
          const country = datum as CountryFeature;
          if (country === selectedCountry) return "#ffb35c";
          if (country.slot?.entries.length) return "#6fd4aa";
          if (country.slot) return "#203e45";
          return "#132a32";
        }}
        polygonSideColor={() => "#07151d"}
        polygonStrokeColor={() => "rgba(154, 226, 220, .24)"}
        polygonAltitude={(datum) => datum === selectedCountry ? 0.018 : 0.006}
        polygonLabel={(datum) => {
          const country = datum as CountryFeature;
          const bid = country.slot?.entries[0];
          return `<div class="earth-globe-tooltip"><b>${country.properties.name ?? "Territory"}</b><span>${bid ? `${bid.name} leads` : country.slot ? "Open for bids" : "Not listed"}</span></div>`;
        }}
        onPolygonHover={(datum) => {
          document.body.style.cursor = datum && (datum as CountryFeature).slot ? "pointer" : "grab";
          onHover((datum as CountryFeature | null)?.properties.name ?? "");
        }}
        onPolygonClick={(datum) => {
          const slot = (datum as CountryFeature).slot;
          if (slot) onSelect(slot);
        }}
        pointsData={points}
        pointLat="lat"
        pointLng="lng"
        pointColor={(datum) => (datum as { selected: boolean }).selected ? "#fff3cb" : "#ffb35c"}
        pointAltitude={(datum) => (datum as { selected: boolean }).selected ? 0.055 : 0.028}
        pointRadius={(datum) => (datum as { selected: boolean }).selected ? 0.55 : 0.25}
        pointsMerge
        arcsData={arcs}
        arcStartLat="startLat"
        arcStartLng="startLng"
        arcEndLat="endLat"
        arcEndLng="endLng"
        arcColor="color"
        arcAltitudeAutoScale={0.3}
        arcStroke={0.34}
        arcDashLength={0.34}
        arcDashGap={0.75}
        arcDashAnimateTime={reducedMotion ? 0 : 2400}
          onGlobeReady={configureGlobe}
        />
      </Suspense>
      <div className="earth-globe-vignette" aria-hidden="true" />
      <div className="earth-globe-instruction">DRAG TO ORBIT · SCROLL TO ZOOM · SELECT A COUNTRY</div>
    </div>
  );
}
